import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Paper, CircularProgress, IconButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useAppDispatch } from 'store/hooks';
import { showToast } from 'store/toastSlice';
import { gamesService } from '../api/games';
import { socketService } from 'services/socketService';
import { formatDate } from 'utils/date';
import styles from './games.module.scss';

interface LiveLogProps {
  session: any;
  siteId: string | number;
  onBack: () => void;
}

export const LiveLog: React.FC<LiveLogProps> = ({ session, siteId, onBack }) => {
  const dispatch = useAppDispatch();
  const [logs, setLogs] = useState<any[]>([]);
  const [isWatching, setIsWatching] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const paperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isMounted = true;

    const startWatching = async () => {
      try {
        const response = await gamesService.watchSession(session.id, siteId);
        if (isMounted) {
          setIsWatching(true);
          if (response?.data?.history && Array.isArray(response.data.history)) {
            setLogs(response.data.history);
          }
        }

        // Some backends expect the client to emit a join or subscribe event after the API call
        const roomName = response?.data?.room || response?.room || `session-watch:${session.id}`;
        const socket = socketService.getSocket();
        if (socket) {
          socket.emit('watchSession', { room: roomName });
        }
      } catch (error: any) {
        dispatch(
          showToast({
            text: error.response?.data?.message || 'Failed to start watching session',
            type: 'error',
          }),
        );
      }
    };

    startWatching();

    const socket = socketService.getSocket();

    const handleLiveBet = (data: any) => {
      // Ensure we only process events for the current session
      if (data.sessionId === session.id) {
        setLogs((prev) => [...prev, data]);
      }
    };

    const handleSessionClosed = (data: any) => {
      if (data.sessionId === session.id) {
        setIsClosed(true);
      }
    };

    if (socket) {
      socket.on('live-bet', handleLiveBet);
      socket.on('session-closed', handleSessionClosed);
      // Just in case the backend emits the event using the room name:
      socket.on(`session-watch:${session.id}`, handleLiveBet);

      // Debug listener to log all incoming events
      socket.onAny((eventName, ...args) => {
        console.log('[Socket Debug] Received event:', eventName, args);
      });
    }

    return () => {
      isMounted = false;
      if (socket) {
        socket.off('live-bet', handleLiveBet);
        socket.off(`session-watch:${session.id}`, handleLiveBet);
        socket.off('session-closed', handleSessionClosed);
      }

      // Attempt to unwatch without blocking unmount
      gamesService.unwatchSession(session.id, siteId).catch((err) => {
        console.error('Failed to unwatch session:', err);
      });

      if (socket) {
        const roomName = `session-watch:${session.id}`;
        socket.emit('unwatchSession', { room: roomName });
      }
    };
  }, [session.id, siteId, dispatch]);

  useEffect(() => {
    // Auto scroll to bottom without scrolling window
    if (paperRef.current) {
      paperRef.current.scrollTo({
        top: paperRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [logs]);

  return (
    <Box
      className={styles.container}
      sx={{
        minHeight: 0,
        height: 'calc(100vh - 200px)',
        maxHeight: 'calc(100vh - 200px)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, p: 3, flexShrink: 0 }}>
        <IconButton
          onClick={onBack}
          sx={{ color: 'var(--text-primary)', bgcolor: 'rgba(255, 255, 255, 0.05)' }}
        >
          <ArrowLeft size={20} />
        </IconButton>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--text-primary)', m: 0 }}>
            Live Log: {session.gameTitle}
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
            Session ID: {session.id} | User: {session.username}
          </Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {isClosed ? (
            <Typography variant="body2" sx={{ color: '#f92672', fontWeight: 600 }}>
              Closed
            </Typography>
          ) : isWatching ? (
            <>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#00e676',
                  animation: 'pulse 1.5s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(0, 230, 118, 0.9)' },
                    '70%': { boxShadow: '0 0 0 12px rgba(0, 230, 118, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(0, 230, 118, 0)' },
                  },
                }}
              />
              <Typography variant="body2" sx={{ color: '#00e676', fontWeight: 600 }}>
                Live
              </Typography>
            </>
          ) : (
            <>
              <CircularProgress size={16} sx={{ color: 'var(--accent-green)' }} />
              <Typography variant="body2" sx={{ color: 'var(--text-secondary)' }}>
                Connecting...
              </Typography>
            </>
          )}
        </Box>
      </Box>

      <Paper
        ref={paperRef}
        sx={{
          flex: 1,
          minHeight: 0,
          bgcolor: '#0a0e17',
          p: 2,
          borderRadius: 2,
          fontFamily: 'monospace',
          overflowY: 'auto',
          border: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        {logs.length === 0 ? (
          <Typography
            sx={{
              color: 'var(--text-secondary)',
              textAlign: 'center',
              mt: 4,
              fontFamily: 'monospace',
            }}
          >
            Waiting for game events...
          </Typography>
        ) : (
          logs.map((log, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 1,
                p: 1.5,
                bgcolor: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.05)',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
              }}
            >
              <Typography sx={{ color: '#8892b0', fontSize: '13px', minWidth: '130px' }}>
                {formatDate(log.timestamp || new Date().toISOString())}
              </Typography>

              <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', gap: 3 }}>
                <Typography sx={{ color: '#e6f1ff', fontSize: '13px' }}>
                  <span style={{ color: '#8892b0', marginRight: '4px' }}>Round:</span>
                  {log.roundId || '-'}
                </Typography>

                <Typography sx={{ color: '#e6f1ff', fontSize: '13px' }}>
                  <span style={{ color: '#8892b0', marginRight: '4px' }}>Balance:</span>
                  {Number(log.balance).toLocaleString()} {log.currencyCode}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: '200px',
                  justifyContent: 'flex-end',
                }}
              >
                {log.transactions?.map((t: any, tIdx: number) => {
                  const isWin = t.type === 'win' || t.type === 'deposit';
                  return (
                    <Box
                      key={tIdx}
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '12px',
                        fontWeight: 600,
                        bgcolor: isWin ? 'rgba(166, 226, 46, 0.1)' : 'rgba(249, 38, 114, 0.1)',
                        color: isWin ? '#a6e22e' : '#f92672',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {t.type.toUpperCase()}: {Number(t.amount).toLocaleString()}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};
